// 型ガード関数

import type { ApiErrorInfo, NetworkErrorInfo, ValidationErrorInfo } from '../types/error-types';

// Error型チェック
export function isError(value: unknown): value is Error {
  return value instanceof Error;
}

// APIError型チェック
export function isApiError(error: unknown): error is ApiErrorInfo {
  return (
    isError(error) &&
    'name' in error &&
    error.name === 'APIError' &&
    'status' in error &&
    'code' in error &&
    typeof (error as ApiErrorInfo).status === 'number' &&
    typeof (error as ApiErrorInfo).code === 'string'
  );
}

// NetworkError型チェック
export function isNetworkError(error: unknown): error is NetworkErrorInfo {
  return isError(error) && 'name' in error && error.name === 'NetworkError';
}

// ValidationError型チェック
export function isValidationError(error: unknown): error is ValidationErrorInfo {
  return (
    isError(error) &&
    'name' in error &&
    error.name === 'ValidationError' &&
    'field' in error &&
    typeof (error as ValidationErrorInfo).field === 'string'
  );
}

// Response型チェック
export function isResponse(value: unknown): value is Response {
  return value instanceof Response;
}

// FormData型チェック
export function isFormData(value: unknown): value is FormData {
  return value instanceof FormData;
}

// Record型チェック
export function isRecord(value: unknown): value is Record<string, unknown> {
  return (
    value !== null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    !(value instanceof Date) &&
    !(value instanceof RegExp) &&
    !isFormData(value)
  );
}

// 文字列配列型チェック
export function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string');
}

// 数値型チェック
export function isNumber(value: unknown): value is number {
  return typeof value === 'number' && !Number.isNaN(value);
}

// 文字列型チェック
export function isString(value: unknown): value is string {
  return typeof value === 'string';
}

// ブーリアン型チェック
export function isBoolean(value: unknown): value is boolean {
  return typeof value === 'boolean';
}

// null/undefined チェック
export function isNullOrUndefined(value: unknown): value is null | undefined {
  return value === null || value === undefined;
}

// 有効な値チェック（null/undefinedではない）
export function isDefined<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

// HTTPステータスコードチェック
export function isSuccessStatus(status: number): boolean {
  return status >= 200 && status < 300;
}

export function isClientError(status: number): boolean {
  return status >= 400 && status < 500;
}

export function isServerError(status: number): boolean {
  return status >= 500;
}

// APIレスポンスの成功判定
export function isApiSuccess<T>(response: unknown): response is { success: true; data: T } {
  return (
    isRecord(response) && 'success' in response && response.success === true && 'data' in response
  );
}

// APIレスポンスのエラー判定
export function isApiErrorResponse(response: unknown): response is {
  success: false;
  error: {
    code: string;
    message: string;
    details?: unknown;
  };
} {
  return (
    isRecord(response) &&
    'success' in response &&
    response.success === false &&
    'error' in response &&
    isRecord(response.error) &&
    'code' in response.error &&
    'message' in response.error &&
    isString(response.error.code) &&
    isString(response.error.message)
  );
}

// ファイル型チェック
export function isFile(value: unknown): value is File {
  return value instanceof File;
}

// Blob型チェック
export function isBlob(value: unknown): value is Blob {
  return value instanceof Blob;
}
